/**
 * Utility functions for generating commodity data for document shipments.
 * FedEx requires commodity descriptions for international customs declarations,
 * even for document shipments.
 */

export interface DocumentCommodity {
  description: string;
  quantity: number;
  weight: number;
  price: number;
  madeIn: string;
}

export interface DocumentInput {
  id?: string;
  quantity: number;
  weight: number;
}

// Default values for document commodities
export const DEFAULT_DOCUMENT_DESCRIPTION = "Business Documents";
export const DEFAULT_DOCUMENT_PRICE = 1; // Nominal value for customs (USD)
export const DEFAULT_DOCUMENT_ORIGIN = "United States";

/**
 * Generate a single commodity object for a document.
 * @param document - The document with quantity and weight
 * @param originCountry - The origin country name for customs (defaults to United States)
 * @returns DocumentCommodity object with all required fields for FedEx
 */
export const generateDocumentCommodity = (
  document: DocumentInput,
  originCountry: string = DEFAULT_DOCUMENT_ORIGIN
): DocumentCommodity => ({
  description: DEFAULT_DOCUMENT_DESCRIPTION,
  quantity: document.quantity || 1,
  weight: document.weight || 0.5,
  price: DEFAULT_DOCUMENT_PRICE,
  madeIn: originCountry || DEFAULT_DOCUMENT_ORIGIN,
});

/**
 * Generate commodity array for multiple documents.
 * Each document becomes a commodity item for customs declaration.
 * @param documents - Array of documents with quantity and weight
 * @param originCountry - The origin country name for customs
 * @returns Array of DocumentCommodity objects
 */
export const generateDocumentCommodities = (
  documents: DocumentInput[],
  originCountry: string = DEFAULT_DOCUMENT_ORIGIN
): DocumentCommodity[] => {
  if (!documents || documents.length === 0) {
    // Return at least one default commodity if no documents provided
    return [generateDocumentCommodity({ quantity: 1, weight: 0.5 }, originCountry)];
  }

  return documents.map(doc => generateDocumentCommodity(doc, originCountry));
};

/**
 * Nominal envelope dimensions (cm) used for document shipments.
 * Documents have no real box dimensions, but the rate APIs (FedEx, ShippingToGo)
 * and backend validation expect every package to carry dimensions, so we send a
 * small large-envelope footprint instead of zeros.
 */
export const DOCUMENT_PARCEL_DIMENSIONS = { length: 30, width: 24, height: 2 };

export interface DocumentParcelInput {
  id?: string;
  quantity: number;
  weight: number;
}

/**
 * Convert a document into the parcel shape expected by the rate/label APIs.
 * Documents are tracked separately in the UI, but the backend only accepts a
 * `parcels` array — so every document must be mapped to a parcel before sending.
 */
export const mapDocumentToParcel = (document: DocumentParcelInput) => {
  // Documents always ship as a single envelope — quantity is locked to 1 in the
  // UI. Keeping it at 1 ensures physicalWeight stays within the FedEx envelope
  // 0.5 kg limit (weight is also capped at 0.5 kg / 1.1 lb in the UI).
  const quantity = 1;
  const weight = document.weight || 0.5;

  return {
    ...(document.id ? { id: document.id } : {}),
    quantity,
    weight,
    length: DOCUMENT_PARCEL_DIMENSIONS.length,
    width: DOCUMENT_PARCEL_DIMENSIONS.width,
    height: DOCUMENT_PARCEL_DIMENSIONS.height,
    physicalWeight: parseFloat((weight * quantity).toFixed(2)),
    dimensionalWeight: 0,
    fedexPackagingType: 'FEDEX_ENVELOPE',
  };
};

/**
 * Convert an array of documents into parcels for a rate/label request.
 * Always returns at least one parcel so document searches never send an empty
 * `parcels` array (which the backend rejects).
 */
export const mapDocumentsToParcels = (documents?: DocumentParcelInput[]) => {
  if (!documents || documents.length === 0) {
    return [mapDocumentToParcel({ quantity: 1, weight: 0.5 })];
  }

  return documents.map(mapDocumentToParcel);
};

export default generateDocumentCommodities;
